import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { CTALink } from "./ui-kit";

const products = [
  { name: "MF, PMS & AIF", to: "/products/mf-pms-aif" },
  { name: "Direct Equity & Trading", to: "/products/direct-equity" },
  { name: "Fixed Income & Insurance", to: "/products/fixed-income-insurance" },
  { name: "DAAE — Alpha Engine", to: "/products/daae" },
];

const resources = [
  { name: "Goal Planning", to: "/goals" },
  { name: "Start Investing", to: "/start-investing" },
  { name: "Build Wealth Through SIP", to: "/sip-investment" },
  { name: "Plan for Retirement", to: "/retirement-planning" },
  { name: "Calculators", to: "/calculators" },
  { name: "Learning", to: "/blog" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const NavLink = ({ to, children }) => (
    <Link
      to={to}
      className="font-sans text-sm font-medium text-offblack hover:text-orange transition-colors"
    >
      {children}
    </Link>
  );

  const Dropdown = ({ label, items, testid }) => (
    <div className="relative group" data-testid={testid}>
      <button className="flex items-center gap-1 font-sans text-sm font-medium text-offblack hover:text-orange transition-colors">
        {label}
        <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
      <div className="absolute left-0 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
        <div className="bg-white border border-black/5 shadow-xl min-w-[260px] py-2">
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.to.replace(/\//g, "-")}`}
              className="block px-6 py-3 font-sans text-sm text-offblack hover:text-orange hover:bg-cream transition-colors"
            >
              {it.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-citrine/30 shadow-elevated" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Logo />

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink to="/business">Business</NavLink>
          <NavLink to="/individual">Individual</NavLink>
          <Dropdown label="Products" items={products} testid="nav-products" />
          <Dropdown label="Resources" items={resources} testid="nav-resources" />
          <NavLink to="/about">About</NavLink>
        </nav>

        <div className="hidden lg:block">
          <CTALink to="/contact" variant="primary" data-testid="nav-contact-cta">
            Talk to us
          </CTALink>
        </div>

        <button
          className="lg:hidden text-navy"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-black/5 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col">
            {[
              { name: "Business", to: "/business" },
              { name: "Individual", to: "/individual" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="py-3 font-sans text-base text-offblack border-b border-black/5">
                {l.name}
              </Link>
            ))}
            {[
              { label: "Products", items: products },
              { label: "Resources", items: resources },
            ].map((g) => (
              <div key={g.label} className="border-b border-black/5">
                <button
                  className="w-full flex items-center justify-between py-3 font-sans text-base text-offblack"
                  onClick={() => setMobileGroup(mobileGroup === g.label ? null : g.label)}
                >
                  {g.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileGroup === g.label ? "rotate-180" : ""}`} />
                </button>
                {mobileGroup === g.label &&
                  g.items.map((it) => (
                    <Link key={it.to} to={it.to} className="block py-2.5 pl-4 font-sans text-sm text-slate">
                      {it.name}
                    </Link>
                  ))}
              </div>
            ))}
            <Link to="/about" className="py-3 font-sans text-base text-offblack border-b border-black/5">
              About
            </Link>
            <Link to="/contact" className="mt-4 bg-orange text-white text-center py-3 font-sans text-sm">
              Talk to us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
